import React, { useState } from 'react';
import { Tag, Link, Search, Plus, AlertTriangle } from 'lucide-react';

const EquipmentTagging = ({ worksheet }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const equipmentDatabase = [
    {
      tag: 'HE-101',
      name: 'Process Heat Exchanger',
      type: 'Heat Exchanger',
      location: 'Unit 100 - Area A',
      linkedHazards: 12,
      status: 'active'
    },
    {
      tag: 'P-205',
      name: 'Feed Pump',
      type: 'Centrifugal Pump',
      location: 'Unit 200 - Area B',
      linkedHazards: 8,
      status: 'active'
    },
    {
      tag: 'TK-304',
      name: 'Storage Tank',
      type: 'Storage Tank',
      location: 'Tank Farm',
      linkedHazards: 15,
      status: 'active'
    }
  ];

  const linkedHazards = [
    {
      id: 'RISK-001',
      equipment: 'HE-101',
      hazard: 'High Temperature Exposure',
      riskRating: 15,
      status: 'open'
    },
    {
      id: 'RISK-002',
      equipment: 'HE-101',
      hazard: 'Chemical Exposure',
      riskRating: 16,
      status: 'open'
    }
  ];

  const filteredEquipment = equipmentDatabase?.filter(eq =>
    eq?.tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    eq?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  if (!worksheet) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">Select a worksheet to manage equipment tagging</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Equipment Tagging</h2>
        <p className="text-sm text-gray-600">
          Link hazards to specific equipment tags for {worksheet?.title}
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Equipment List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Equipment Database</h3>
            <button className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
              <Plus className="w-4 h-4" />
              Add Equipment
            </button>
          </div>

          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search equipment..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="space-y-2">
            {filteredEquipment?.map((equipment) => (
              <button
                key={equipment?.tag}
                onClick={() => setSelectedEquipment(equipment)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  selectedEquipment?.tag === equipment?.tag
                    ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-gray-900">{equipment?.tag}</span>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                    {equipment?.status}
                  </span>
                </div>
                <p className="text-sm text-gray-700 mb-1">{equipment?.name}</p>
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span>{equipment?.type}</span>
                  <span className="flex items-center gap-1">
                    <Link className="w-3 h-3" />
                    {equipment?.linkedHazards} hazards
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Linked Hazards */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Linked Hazards
            {selectedEquipment && ` - ${selectedEquipment?.tag}`}
          </h3>

          {selectedEquipment ? (
            <div className="space-y-3">
              {linkedHazards?.filter(h => h?.equipment === selectedEquipment?.tag)?.map((hazard) => (
                  <div
                    key={hazard?.id}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-300"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">{hazard?.id}</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                        Risk: {hazard?.riskRating}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{hazard?.hazard}</p>
                  </div>
                ))}

              <button className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors">
                <Plus className="w-5 h-5 mx-auto mb-1" />
                <span className="text-sm">Link New Hazard</span>
              </button>
            </div>
          ) : (
            <div className="text-center py-12">
              <Tag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-600">Select equipment to view linked hazards</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EquipmentTagging;